<template>
  <div v-show="state.prompt" class="privacy-consent-layer" @touchmove.self.prevent @keydown.esc.stop.prevent @keydown.tab="trapFocus">
    <section
      ref="dialog"
      class="privacy-consent-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-consent-title"
      tabindex="-1"
    >
      <h2 id="privacy-consent-title">{{ title }}</h2>
      <div class="privacy-consent-content">
        <p v-if="state.prompt === 'initial'">
          感谢您使用滇医通平台，在使用前，请您仔细阅读并同意
          <router-link to="/privacy-policy/user_agreement">《用户服务协议》</router-link>，
          <router-link to="/privacy-policy/privacy">《隐私政策》</router-link>，我们将严格保护您的个人信息安全。
        </p>
        <p v-else-if="state.prompt === 'refusal'">
          您拒绝接受最新的<template v-for="(doc, index) in relevantDocuments"><span :key="doc.doc_type"><span v-if="index">、</span><router-link :to="'/privacy-policy/' + doc.doc_type">《{{ documentName(doc.doc_type) }}》</router-link></span></template>，部分功能的使用会受限，若要使用完整的功能，请同意最新的{{ relevantNames }}
        </p>
        <p v-else>
          我们对<template v-for="(doc, index) in relevantDocuments"><span :key="'updated-' + doc.doc_type"><span v-if="index">、</span><router-link :to="'/privacy-policy/' + doc.doc_type">《{{ documentName(doc.doc_type) }}》</router-link></span></template>进行了更新，请您在继续使用本系统前点击查看更新后的<template v-for="(doc, index) in relevantDocuments"><span :key="'read-' + doc.doc_type"><span v-if="index">、</span><router-link :to="'/privacy-policy/' + doc.doc_type">《{{ documentName(doc.doc_type) }}》</router-link></span></template>仔细阅读。<br />
          如您同意以上<template v-for="(doc, index) in relevantDocuments"><span :key="'accept-' + doc.doc_type"><span v-if="index">、</span><router-link :to="'/privacy-policy/' + doc.doc_type">《{{ documentName(doc.doc_type) }}》</router-link></span></template>的全部内容，请点击“同意并继续”，继续使用我们的产品与服务
        </p>
        <p v-if="state.errorMessage" class="privacy-consent-error" role="alert">{{ state.errorMessage }}</p>
      </div>
      <div class="privacy-consent-actions">
        <template v-if="state.prompt === 'refusal'">
          <button type="button" @click="controller.decline()">仍然拒绝</button>
          <button type="button" class="privacy-consent-primary" @click="controller.review()">重新查看</button>
        </template>
        <template v-else>
          <button type="button" @click="controller.reject()">拒绝</button>
          <button type="button" class="privacy-consent-primary" @click="controller.accept()">同意并继续</button>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
import { getCurrentPrivacyPolicies } from '@/apis/fromBaseApi/privacy_policy'
import { createPrivacyConsent } from '@/privacy/consent'

export default {
  name: 'PrivacyConsent',
  props: {
    manual: { type: Boolean, default: false }
  },
  data() {
    const controller = createPrivacyConsent({
      storage: {
        getItem: key => window.localStorage.getItem(key),
        setItem: (key, value) => window.localStorage.setItem(key, value),
        removeItem: key => window.localStorage.removeItem(key)
      },
      management: this.manual,
      fetchCurrent: getCurrentPrivacyPolicies,
      onError: error => this.$toast(error.message)
    })
    return { controller, state: controller.state }
  },
  computed: {
    title() {
      if (this.state.prompt === 'refusal') return '温馨提示'
      if (this.state.prompt === 'update') return this.relevantNames + '更新'
      return '隐私政策提示'
    },
    relevantDocuments() {
      // 首次拒绝文案沿用原型；更新时准确列出需要重新同意的协议。
      return this.state.mode === 'update' ? this.state.changedDocuments : [{ doc_type: 'privacy' }]
    },
    relevantNames() {
      return this.relevantDocuments.map(doc => this.documentName(doc.doc_type)).join('、')
    }
  },
  watch: {
    '$route.fullPath': {
      immediate: true,
      handler() {
        if (!this.manual) this.controller.onRoute(this.$route)
      }
    },
    'state.agreed': {
      handler(agreed) { this.$emit('agreement-change', agreed) }
    },
    'state.prompt'(prompt, previous) {
      if (prompt) {
        if (!previous) this.previousFocus = document.activeElement
        this.$nextTick(() => this.$refs.dialog && this.$refs.dialog.focus())
      } else if (this.previousFocus && document.contains(this.previousFocus)) {
        this.previousFocus.focus()
      }
    }
  },
  mounted() {
    // 脱离页面堆叠上下文，覆盖广告、活动、客服及其他业务弹层。
    document.body.appendChild(this.$el)
  },
  beforeDestroy() {
    this.controller.dispose()
    if (this.$el.parentNode) this.$el.parentNode.removeChild(this.$el)
  },
  methods: {
    open() {
      return this.controller.openManagement()
    },
    documentName(type) {
      return type === 'user_agreement' ? '用户服务协议' : '隐私政策'
    },
    trapFocus(event) {
      const elements = this.$refs.dialog.querySelectorAll('a[href], button')
      const first = elements[0]
      const last = elements[elements.length - 1]
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.$refs.dialog)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }
}
</script>

<style lang="less" scoped>
.privacy-consent-layer {
  position: fixed;
  z-index: 2147483647;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: #111;
}
.privacy-consent-dialog {
  width: 90%;
  max-width: 8.4rem;
  max-height: 85%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0.24rem;
  background: #fff;
  outline: none;
  font-size: 0.36rem;
  line-height: 1.6;
  h2 {
    flex: none;
    margin: 0;
    padding: 0.44rem 0.4rem 0.25rem;
    text-align: center;
    font-size: 0.36rem;
    font-weight: normal;
  }
  a {
    color: #164d7e;
    text-decoration: underline;
    text-underline-offset: 0.06rem;
  }
}
.privacy-consent-content {
  padding: 0 0.56rem 0.4rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  p { margin: 0; }
  .privacy-consent-error {
    margin-top: 0.16rem;
    color: #c0392b;
    font-size: 0.28rem;
  }
}
.privacy-consent-actions {
  display: flex;
  flex: none;
  border-top: 1px solid #eee;
  button {
    flex: 1;
    min-height: 1.12rem;
    padding: 0.2rem 0.12rem;
    border: 0;
    border-radius: 0;
    background: #fff;
    color: #111;
    font: inherit;
    cursor: pointer;
    & + button { border-left: 1px solid #eee; }
  }
  .privacy-consent-primary { font-weight: bold; }
}
</style>
